import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { DashboardLayout } from "@/components/layout/DashboardLayout";
import { StatCard } from "@/components/StatCard";
import api from "@/lib/api";
import { Wrench, Clock, FileText, Droplets, Activity, QrCode, CheckCircle2, Ban, Coins, TrendingDown, BellRing, UserCheck, AlertTriangle } from "lucide-react";

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [nearInactive, setNearInactive] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/dashboard/stats").then(({ data }) => setStats(data)).catch(() => {});
    api.get("/admin/near-inactive").then(({ data }) => setNearInactive(data.installers || [])).catch(() => {});
  }, []);

  const cards = [
    { label: "Total Installers", value: stats?.total_installers ?? 0, icon: Wrench, accent: true, sub: "Registered accounts" },
    { label: "Pending Installers", value: stats?.pending_installers ?? 0, icon: Clock, sub: "Awaiting approval" },
    { label: "Certificates Generated", value: stats?.certificates_generated ?? 0, icon: FileText, sub: "Across all installers" },
    { label: "Bottle Activations", value: stats?.bottle_activations ?? 0, icon: Droplets, sub: "Codes redeemed" },
  ];

  const codeCards = [
    { label: "Total Bottle Codes", value: stats?.total_bottle_codes ?? 0, icon: QrCode, accent: true, sub: "All generated" },
    { label: "Available Codes", value: stats?.available_codes ?? 0, icon: CheckCircle2, sub: "Ready to redeem" },
    { label: "Redeemed Codes", value: stats?.redeemed_codes ?? 0, icon: Droplets, sub: "Activated" },
    { label: "Disabled Codes", value: stats?.disabled_codes ?? 0, icon: Ban, sub: "Blocked" },
    { label: "Total Credits Issued", value: stats?.total_credits_issued ?? 0, icon: Coins, sub: "All-time" },
    { label: "Total Credits Used", value: stats?.total_credits_used ?? 0, icon: TrendingDown, sub: "Certificates" },
    { label: "Activations This Month", value: stats?.activations_this_month ?? 0, icon: Activity, sub: "Current month" },
  ];

  const recent = stats?.recent_activity ?? [];

  const followUpCards = [
    { label: "Follow-Ups Due", value: stats?.follow_ups_due ?? 0, icon: BellRing, accent: true, sub: "Awaiting installer" },
    { label: "Overdue Follow-Ups", value: stats?.follow_ups_overdue ?? 0, icon: Clock, sub: "Past due date" },
    { label: "Completed Follow-Ups", value: stats?.follow_ups_completed ?? 0, icon: CheckCircle2, sub: "Contacted customers" },
    { label: "Skipped Follow-Ups", value: stats?.follow_ups_skipped ?? 0, icon: Ban, sub: "Skipped by installer" },
  ];

  return (
    <DashboardLayout title="Dashboard">
      <div className="mb-8">
        <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-[#E30613]">Command Center</p>
        <h2 className="mt-2 font-display text-2xl font-semibold tracking-tight text-white">Platform overview</h2>
        <p className="mt-1 text-sm text-neutral-500">Monitor installers, certificates, credits and activations across SGBINOXE.</p>
      </div>

      {(stats?.pending_installers ?? 0) > 0 && (
        <button
          onClick={() => navigate("/installers?status=pending_approval")}
          className="mb-6 flex w-full items-center justify-between gap-3 rounded-xl border border-amber-500/30 bg-amber-500/10 px-5 py-4 text-left transition-colors hover:bg-amber-500/[0.16]"
          data-testid="pending-approvals-banner"
        >
          <div className="flex items-center gap-3">
            <UserCheck className="h-5 w-5 shrink-0 text-amber-400" />
            <div>
              <p className="text-sm font-medium text-amber-400">{stats.pending_installers} installer{stats.pending_installers > 1 ? "s" : ""} awaiting approval</p>
              <p className="text-xs text-amber-400/70">Review and approve new sign-ups to give them access.</p>
            </div>
          </div>
          <span className="rounded-lg bg-amber-500/20 px-3 py-1.5 text-xs font-medium text-amber-300">Review →</span>
        </button>
      )}

      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 xl:grid-cols-4">
        {cards.map((c, i) => (<StatCard key={c.label} {...c} delay={i * 60} />))}
      </div>

      <h3 className="mb-4 mt-10 font-display text-base font-medium text-white">Activation Codes & Credits</h3>
      <div className="grid grid-cols-2 gap-4 md:grid-cols-3 xl:grid-cols-4">
        {codeCards.map((c, i) => (<StatCard key={c.label} {...c} delay={i * 50} />))}
      </div>

      <h3 className="mb-4 mt-10 font-display text-base font-medium text-white">Customer Follow-Ups</h3>
      <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
        {followUpCards.map((c, i) => (<StatCard key={c.label} {...c} delay={i * 50} onClick={() => navigate("/follow-ups")} />))}
      </div>

      {nearInactive.length > 0 && (
        <div className="mt-6 rounded-2xl border border-[#E30613]/25 bg-[#141414] p-6" data-testid="near-inactive-widget">
          <div className="mb-5 flex items-center gap-2">
            <AlertTriangle className="h-4 w-4 text-[#FF6069]" />
            <h3 className="font-display text-base font-medium text-white">Installers Near Inactive Status</h3>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[11px] uppercase tracking-wider text-neutral-500">
                  <th className="pb-3">Business Name</th>
                  <th className="pb-3">Available Credits</th>
                  <th className="pb-3">Days Remaining</th>
                  <th className="pb-3">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/[0.05]">
                {nearInactive.map((r) => (
                  <tr key={r.id} data-testid="near-inactive-row">
                    <td className="py-3 text-white">{r.business_name}</td>
                    <td className="py-3 text-neutral-300">{r.available_credits}</td>
                    <td className="py-3">
                      <span className={r.status === "inactive" ? "text-neutral-500" : r.days_remaining <= 3 ? "font-semibold text-[#FF6069]" : "text-amber-400"}>
                        {r.status === "inactive" ? "—" : `${r.days_remaining} day${r.days_remaining !== 1 ? "s" : ""}`}
                      </span>
                    </td>
                    <td className="py-3">
                      {r.status === "inactive"
                        ? <span className="rounded bg-[#E30613]/15 px-2 py-1 text-xs text-[#FF6069]">Inactive</span>
                        : <span className="rounded bg-amber-500/15 px-2 py-1 text-xs text-amber-400">Grace Period</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div className="mt-6 rounded-2xl border border-white/[0.06] bg-[#141414] p-6">
        <div className="mb-5 flex items-center gap-2">
          <Activity className="h-4 w-4 text-[#FF1C2A]" />
          <h3 className="font-display text-base font-medium text-white">Recent Activity</h3>
        </div>
        {recent.length === 0 ? (
          <div className="flex flex-col items-center justify-center rounded-xl border border-dashed border-white/[0.08] py-16 text-center">
            <p className="text-sm text-neutral-500">No activity yet</p>
            <p className="mt-1 text-xs text-neutral-600">New registrations, activations and certificate events will appear here.</p>
          </div>
        ) : (
          <div className="divide-y divide-white/[0.05]">
            {recent.map((a) => (
              <div key={a.id} className="flex items-start justify-between gap-4 py-3" data-testid="admin-recent-activity-row">
                <div>
                  <p className="text-sm text-white">{a.description}</p>
                  {a.actor_name && <p className="text-xs text-neutral-600">by {a.actor_name}</p>}
                </div>
                <p className="whitespace-nowrap text-xs text-neutral-500">{new Date(a.created_at).toLocaleString()}</p>
              </div>
            ))}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
